"use client"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import Text from "@/app/(site)/_components/Atoms/Text"
import AddToCartButton from "@/app/(site)/_components/Modules/ProductDetail/AddToCartButton"
import FitType from "@/app/(site)/_components/Modules/ProductDetail/FitType"
import SizeChart from "@/app/(site)/_components/Modules/ProductDetail/SizeChart"
import { BreadcrumbItem } from "@/app/(site)/_components/Molecules/Breadcrumbs"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { extractSizeInfo } from "@/app/(site)/_components/Page/Product/helpers"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import {
    selectSize,
    setAvailableSizes,
    useAppDispatch,
    useAppSelector
} from "@/app/(site)/contexts/Global"
import {
    ProductData,
    RelatedVariant
} from "@/lib/content-service/types/ContentServiceProduct"
import logger from "@/lib/logger/logger-init"
import { viewItemDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { viewItemEvent } from "@/lib/utils/tracking/gtmEvents"
import { t } from "@/translations"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import { useRouter } from "next/navigation"
import React, { ReactNode, useEffect, useMemo, useState } from "react"
import { quickShopProductHelperFunction, useGetProductLangAndCategory } from "./helpers"

type QuickShopFormType = {
    initialProductValue: CheckoutAPIProduct | RelatedProduct | ProductData
    relatedVariantsFitType: string[]
    productFitType: string
    product: CheckoutAPIProduct
    allRelatedVariants: (
        | CheckoutAPIProduct
        | RelatedProduct
        | ProductData
        | RelatedVariant
    )[]
    children?: ReactNode
    handleClose: () => void
}

const QuickShopForm: React.FC<QuickShopFormType> = ({
    initialProductValue,
    relatedVariantsFitType,
    productFitType,
    product,
    allRelatedVariants,
    handleClose,
    children
}) => {
    const router = useRouter()
    const dispatch = useAppDispatch()
    const availableSizesContext = useAppSelector(state => state.product.availableSizes)
    const initialSelectedSize = useAppSelector(state => state.product.selectedSize)
    const { langCode, canonicalCategory } = useGetProductLangAndCategory(product)
    const setVariantCategoryName = useEcommerceStore(
        state => state.setVariantCategoryName
    )

    const [errorStyles, setErrorStyles] = useState(false)
    const [fitType, setFitType] = useState(productFitType)
    const [filteredVariants, setFilteredVariants] = useState([
        ...allRelatedVariants,
        product
    ])
    const [isPageLoaded, setIsPageLoaded] = useState(false)

    const sizes = useMemo(
        () => extractSizeInfo(initialProductValue),
        [initialProductValue]
    )

    // Synchronize available sizes and trigger events on initial load
    useEffect(() => {
        if (JSON.stringify(availableSizesContext) !== JSON.stringify(sizes)) {
            dispatch(setAvailableSizes(sizes))
        }

        if (!isPageLoaded) {
            const firstAvailableSize = sizes.find(size => size.isAvailable)
            if (firstAvailableSize) {
                dispatch(selectSize(firstAvailableSize.name))
                viewItemEvent(product, firstAvailableSize)
                viewItemDepictEvent(product, firstAvailableSize)
            }
            setIsPageLoaded(true)
        }
    }, [product.productSku, sizes, isPageLoaded])

    // Update the fit type and handle variant switching
    useEffect(() => {
        const relevantVariant = allRelatedVariants.find(
            variant => variant?.fit_type_fit_type === fitType
        )

        if (
            relevantVariant?.uri &&
            relevantVariant?.fit_type_fit_type !== product.fit_type_fit_type
        ) {
            // @ts-expect-error 'shallow' does not exist in type 'NavigateOptions' See: https://github.com/vercel/next.js/issues/60007

            router.replace(relevantVariant.uri, { shallow: true })
        } else if (!relevantVariant) {
            logger.warn("Relevant variant not found for fit type:", fitType)
        }
    }, [fitType, allRelatedVariants, product.fit_type_fit_type, router])

    // Configure variant category breadcrumbs
    useEffect(() => {
        if (initialProductValue?.categoryUri) {
            const breadcrumbs = initialProductValue.categoryUri
                .split("/")
                .reduce<BreadcrumbItem[]>((acc, _, index, array) => {
                    const url = "/" + array.slice(0, index + 1).join("/")
                    return [
                        ...acc,
                        { name: initialProductValue.categoryName[index], url }
                    ]
                }, [])
            setVariantCategoryName(breadcrumbs)
        }
    }, [initialProductValue, setVariantCategoryName])

    // Extract product details
    const {
        productColorName,
        productLensName,
        isAvailableButNotSelected,
        isAnySizeSelected,
        isSelectedSizeAvailable,
        isOneSize,
        productName,
        productPrice,
        productUrl
    } = quickShopProductHelperFunction(product, availableSizesContext)

    const isFitType =
        Array.isArray(relatedVariantsFitType) && relatedVariantsFitType.length > 0

    return (
        <form
            className="w-full overflow-auto md:overflow-hidden md:pb-0"
            aria-label="Product details form"
        >
            <Text size="quickShop" weight="light" className="text-main-dark-black">
                {productName}
            </Text>
            <Text size="medium" weight="light" className="text-main-dark-black">
                {productPrice}
            </Text>

            {isFitType && (
                <FitType
                    relatedVariantsFitType={relatedVariantsFitType}
                    relatedVariants={allRelatedVariants}
                    setFilteredAllProducts={setFilteredVariants}
                    setFitType={setFitType}
                    fitType={fitType}
                    isQuickShop
                />
            )}

            <div className="mb-8 border-b border-b-main-medium-grey pb-8 md:mb-16 md:pb-24">
                <div className="flex justify-between">
                    <div className="mb-15 flex gap-4 sm:mb-8">
                        <Text size="small" weight="regular">
                            {t(langCode, "product_page.color")}
                        </Text>
                        <Text
                            size="small"
                            weight="regular"
                            className="text-main-dark-grey"
                        >
                            {productColorName || ""}
                        </Text>
                    </div>

                    {product.lens && (
                        <div className="flex gap-4">
                            <Text size="small" weight="regular">
                                {t(langCode, "product_page.lens")}
                            </Text>
                            <Text
                                size="small"
                                weight="regular"
                                className="text-main-dark-grey"
                            >
                                {productLensName || ""}
                            </Text>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-8 md:mt-16">{children}</div>
            </div>

            <div className="mb-16 border-b border-b-main-medium-grey pb-12 md:mb-32 md:pb-24">
                <div className="mb-16 flex justify-between">
                    <Text size="small" weight="regular">
                        {t(langCode, "product_page.size")}&#x3A;&nbsp;
                        {isOneSize
                            ? t(langCode, "product_page.one_size")
                            : isAvailableButNotSelected && !isAnySizeSelected
                              ? t(langCode, "product_page.select_size")
                              : isSelectedSizeAvailable
                                ? initialSelectedSize
                                : ""}
                    </Text>
                </div>

                <div className="flex gap-16 md:mb-12">
                    <SizeChart
                        sizes={sizes}
                        setErrorStyles={setErrorStyles}
                        errorStyles={errorStyles}
                    />
                </div>
            </div>

            <div className="pb-16 md:pb-24">
                <AddToCartButton
                    setErrorStyles={setErrorStyles}
                    product={product}
                    isQuickShop={true}
                    aria-label="Add to cart"
                />
            </div>

            <DynamicLink
                href={`/product/${productUrl}`}
                role="button"
                onClick={handleClose}
                aria-label={`View more details for ${productName}`}
            >
                <Text size="medium" weight="regular" className="flex uppercase">
                    {t(langCode, "product_page.view_more_product_info")}
                    <IconArrowRight className="ml-6 inline h-20 w-20" />
                </Text>
            </DynamicLink>
        </form>
    )
}

export default QuickShopForm
